import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, FileText, Check, Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import api from '../utils/api';
import { formatCurrency, formatDate } from '../utils/helpers';
import { toast } from 'sonner';

export const InvoiceGeneratePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCustomer = searchParams.get('customer');
  const preselectedWorkOrder = searchParams.get('workOrder');
  
  const [allApprovedWorkOrders, setAllApprovedWorkOrders] = useState([]);
  const [customersWithApproved, setCustomersWithApproved] = useState([]);
  const [customers, setCustomers] = useState({});
  const [sites, setSites] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerOpen, setCustomerOpen] = useState(false);
  const [selectedWorkOrders, setSelectedWorkOrders] = useState([]);
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = newest first, 'asc' = oldest first
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all approved work orders
      const woResponse = await api.getWorkOrders({ status: 'APPROVED' });
      const workOrders = woResponse.data;
      setAllApprovedWorkOrders(workOrders);

      // Load customers for name mapping
      const custResponse = await api.getCustomers();
      const custMap = {};
      custResponse.data.forEach(c => {
        custMap[c.id] = c.name_or_company;
      });
      setCustomers(custMap);

      // Build list of customers with approved work orders
      const customerIds = [...new Set(workOrders.map(wo => wo.customer_id))];
      const customersWithWO = customerIds.map(id => ({
        id,
        name: custMap[id] || 'Cliente',
        count: workOrders.filter(wo => wo.customer_id === id).length
      }));
      setCustomersWithApproved(customersWithWO);

      // Load sites for all customers that have approved WOs
      const sitesMap = {};
      for (const customerId of customerIds) {
        try {
          const sitesResponse = await api.getCustomerSites(customerId);
          sitesResponse.data.forEach(s => {
            sitesMap[s.id] = s.site_name;
          });
        } catch (e) {
          console.error('Failed to load sites for customer', customerId);
        }
      }
      setSites(sitesMap);

    } catch (error) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  // Apply preselection from URL params after data is loaded
  useEffect(() => {
    if (!loading && !initialized && allApprovedWorkOrders.length > 0) {
      if (preselectedCustomer) {
        setSelectedCustomer(preselectedCustomer);
        if (preselectedWorkOrder) {
          // Check if the work order exists and belongs to this customer
          const wo = allApprovedWorkOrders.find(w => w.id === preselectedWorkOrder && w.customer_id === preselectedCustomer);
          if (wo) {
            setSelectedWorkOrders([preselectedWorkOrder]);
          }
        }
      }
      setInitialized(true);
    }
  }, [loading, initialized, allApprovedWorkOrders, preselectedCustomer, preselectedWorkOrder]);

  // Filter customers for search
  const filteredCustomersForSearch = useMemo(() => {
    if (!customerSearch) return customersWithApproved;
    return customersWithApproved.filter(c => 
      c.name.toLowerCase().includes(customerSearch.toLowerCase())
    );
  }, [customersWithApproved, customerSearch]);

  // Filter and sort work orders
  const filteredWorkOrders = useMemo(() => {
    let filtered = selectedCustomer
      ? allApprovedWorkOrders.filter(wo => wo.customer_id === selectedCustomer)
      : allApprovedWorkOrders;
    
    // Sort by date
    filtered = [...filtered].sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
    
    return filtered;
  }, [allApprovedWorkOrders, selectedCustomer, sortOrder]);

  // Get the customer ID of the first selected work order (to enforce same-customer selection)
  const selectedCustomerId = useMemo(() => {
    if (selectedWorkOrders.length === 0) return null;
    const firstSelected = allApprovedWorkOrders.find(wo => wo.id === selectedWorkOrders[0]);
    return firstSelected?.customer_id || null;
  }, [selectedWorkOrders, allApprovedWorkOrders]);

  const toggleWorkOrder = (workOrderId) => {
    const wo = allApprovedWorkOrders.find(w => w.id === workOrderId);
    if (!wo) return;

    setSelectedWorkOrders(prev => {
      if (prev.includes(workOrderId)) {
        // Deselecting
        return prev.filter(id => id !== workOrderId);
      } else {
        // Selecting - check if same customer
        if (prev.length === 0) {
          // First selection
          return [workOrderId];
        }
        // Check if same customer as already selected
        const firstSelectedWO = allApprovedWorkOrders.find(w => w.id === prev[0]);
        if (firstSelectedWO && firstSelectedWO.customer_id === wo.customer_id) {
          return [...prev, workOrderId];
        } else {
          // Different customer - replace selection
          toast.info('Puoi selezionare solo bollettini dello stesso cliente');
          return [workOrderId];
        }
      }
    });
  };

  const toggleAll = () => {
    // Only toggle work orders that match the current filter AND the selected customer (if any)
    const eligibleWOs = filteredWorkOrders.filter(wo => 
      !selectedCustomerId || wo.customer_id === selectedCustomerId
    );
    
    if (selectedWorkOrders.length === eligibleWOs.length && eligibleWOs.length > 0) {
      setSelectedWorkOrders([]);
    } else {
      setSelectedWorkOrders(eligibleWOs.map(wo => wo.id));
    }
  };

  const handleCustomerChange = (customerId) => {
    setSelectedCustomer(customerId);
    setCustomerSearch('');
    setCustomerOpen(false);
    // Clear selection when changing filter, unless selecting same customer as already selected WOs
    if (selectedCustomerId && customerId !== selectedCustomerId) {
      setSelectedWorkOrders([]);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  const calculateTotals = () => {
    const selected = allApprovedWorkOrders.filter(wo => selectedWorkOrders.includes(wo.id));
    const net = selected.reduce((sum, wo) => sum + (wo.net_total || 0), 0);
    const mwst = selected.reduce((sum, wo) => sum + (wo.mwst_total || 0), 0);
    const gross = selected.reduce((sum, wo) => sum + (wo.gross_total || 0), 0);
    return { net, mwst, gross };
  };

  const handleGenerate = async () => {
    if (selectedWorkOrders.length === 0) {
      toast.error('Seleziona almeno un bollettino');
      return;
    }

    setGenerating(true);
    try {
      const response = await api.generateInvoice(selectedWorkOrders);
      toast.success(`Fattura ${response.data.number} generata con successo`);
      navigate(`/app/invoices/${response.data.id}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nella generazione fattura';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const formatUserName = (name) => {
    if (!name) return '-';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}. ${parts.slice(1).join(' ')}`;
    }
    return name;
  };

  const totals = calculateTotals();

  return (
    <div data-testid="invoice-generate-page">
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/app/invoices')}
          className="mb-4"
        >
          <ArrowLeft size={18} className="mr-2" />
          Torna alle Fatture
        </Button>
        <h1 className="font-heading text-4xl font-bold text-slate-900">Genera Fattura</h1>
        <p className="text-slate-600 mt-2">Seleziona i bollettini approvati da fatturare</p>
      </div>

      {/* Customer Filter */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Customer Filter with Search */}
            <div className="w-72">
              <Label className="text-xs text-slate-500 mb-1 block">Cliente</Label>
              <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={customerOpen}
                    className="w-full justify-between font-normal"
                    data-testid="customer-filter"
                  >
                    {selectedCustomer ? customers[selectedCustomer] : "Tutti i clienti"}
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Cerca cliente..." 
                      value={customerSearch}
                      onValueChange={setCustomerSearch}
                    />
                    <CommandList>
                      <CommandEmpty>Nessun cliente trovato</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="__all__"
                          onSelect={() => handleCustomerChange('')}
                        >
                          <span>Tutti i clienti</span>
                          <span className="text-xs text-slate-400 ml-2">({allApprovedWorkOrders.length})</span>
                        </CommandItem>
                        {filteredCustomersForSearch.map(customer => (
                          <CommandItem
                            key={customer.id}
                            value={customer.name}
                            onSelect={() => handleCustomerChange(customer.id)}
                          >
                            <span>{customer.name}</span>
                            <span className="text-xs text-slate-400 ml-2">({customer.count})</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Sort Order */}
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Ordina per data</Label>
              <Button
                variant="outline"
                onClick={toggleSortOrder}
                className="gap-2"
              >
                {sortOrder === 'desc' ? (
                  <>
                    <ArrowDown size={16} />
                    Più recenti
                  </>
                ) : (
                  <>
                    <ArrowUp size={16} />
                    Più vecchi
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Work Orders Table */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>
              Bollettini Approvati
              {selectedCustomer && ` - ${customers[selectedCustomer]}`}
            </span>
            {filteredWorkOrders.length > 0 && (selectedCustomer || selectedWorkOrders.length > 0) && (
              <Button variant="outline" size="sm" onClick={toggleAll}>
                {selectedWorkOrders.length === filteredWorkOrders.length && filteredWorkOrders.length > 0 
                  ? 'Deseleziona Tutti' 
                  : 'Seleziona Tutti'}
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-slate-100 animate-pulse rounded"></div>
              ))}
            </div>
          ) : filteredWorkOrders.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nessun bollettino approvato</p>
              <p className="text-sm mt-2">I bollettini devono essere approvati prima di poter essere fatturati</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary border-b">
                  <tr>
                    <th className="w-12 p-4"></th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Numero</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Cliente</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Progetto</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Creato da</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Data</th>
                    <th className="text-right p-4 text-sm font-semibold text-slate-700">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWorkOrders.map(wo => {
                    // Check if this WO can be selected (same customer as already selected, or no selection yet)
                    const canSelectThis = !selectedCustomerId || wo.customer_id === selectedCustomerId;
                    const isSelected = selectedWorkOrders.includes(wo.id);
                    
                    return (
                      <tr
                        key={wo.id}
                        className={`border-b transition-colors cursor-pointer hover:bg-secondary ${
                          isSelected ? 'bg-primary/5' : ''
                        } ${!canSelectThis ? 'opacity-50' : ''}`}
                        onClick={() => toggleWorkOrder(wo.id)}
                        data-testid={`wo-row-${wo.id}`}
                      >
                        <td className="p-4" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleWorkOrder(wo.id)}
                            disabled={!canSelectThis && !isSelected}
                          />
                        </td>
                        <td className="p-4">
                          <div className="font-mono font-medium text-slate-900">{wo.number}</div>
                        </td>
                        <td className="p-4 text-slate-700 font-medium">
                          {customers[wo.customer_id] || 'Cliente'}
                        </td>
                        <td className="p-4 text-slate-600">
                          {wo.site_id ? sites[wo.site_id] || '-' : '-'}
                        </td>
                        <td className="p-4 text-slate-600 text-sm">
                          {formatUserName(wo.created_by_name)}
                        </td>
                        <td className="p-4 text-slate-600">{formatDate(wo.created_at)}</td>
                        <td className="p-4 text-right font-mono">
                          {wo.gross_total ? formatCurrency(wo.gross_total) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary and Generate Button */}
      {selectedWorkOrders.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Riepilogo Fattura</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-slate-600 mb-2">
                  <span className="font-medium">{selectedWorkOrders.length}</span> bollettini selezionati per <span className="font-medium">{customers[selectedCustomerId] || 'Cliente'}</span>
                </p>
                <div className="text-sm text-slate-500">
                  Bollettini: {allApprovedWorkOrders
                    .filter(wo => selectedWorkOrders.includes(wo.id))
                    .map(wo => wo.number)
                    .join(', ')}
                </div>
              </div>
              <div className="text-right space-y-1">
                <div className="flex justify-between gap-8 text-sm">
                  <span className="text-slate-600">Netto:</span>
                  <span className="font-mono">{formatCurrency(totals.net)}</span>
                </div>
                <div className="flex justify-between gap-8 text-sm">
                  <span className="text-slate-600">IVA:</span>
                  <span className="font-mono">{formatCurrency(totals.mwst)}</span>
                </div>
                <div className="flex justify-between gap-8 font-medium border-t pt-1">
                  <span>Totale:</span>
                  <span className="font-mono text-lg">{formatCurrency(totals.gross)}</span>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <Button
                size="lg"
                onClick={handleGenerate}
                disabled={generating}
                data-testid="generate-invoice-btn"
              >
                <Check size={18} className="mr-2" />
                {generating ? 'Generazione...' : 'Genera Fattura'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
